import { useEffect, useRef, useState } from "react";

const safeParse = (raw, fallback) => {
    try {
        return raw == null ? fallback : JSON.parse(raw);
    } catch {
        return fallback;
    }
};

/**
 * Persist React state in localStorage (or sessionStorage).
 * - Debounced writes
 * - Optional versioning + migration
 */
export default function usePersistentState(
    storageKey,
    initialValue,
    {
        storage = typeof window !== "undefined" ? window.localStorage : null,
        version = 1,
        migrate = (v) => v, // (oldValue) => newValue
        debounceMs = 250,
    } = {}
) {
    const [state, setState] = useState(() => {
        if (!storage) return initialValue;
        const raw = storage.getItem(storageKey);
        const saved = safeParse(raw, null);

        // Versioned payload { v, data }
        if (saved && typeof saved === "object" && "v" in saved) {
            if (saved.v === version) return saved.data;
            return migrate(saved.data);
        }

        // Unversioned legacy
        return saved ?? initialValue;
    });

    const timer = useRef(null);
    useEffect(() => {
        if (!storage) return;
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            try {
                storage.setItem(storageKey, JSON.stringify({ v: version, data: state }));
            } catch {
                // ignore quota/security errors
            }
        }, debounceMs);
        return () => timer.current && clearTimeout(timer.current);
    }, [state, storageKey, storage, version, debounceMs]);

    const clear = () => {
        try { storage?.removeItem(storageKey); } catch { }
        setState(initialValue);
    };

    return [state, setState, clear];
}
